import { getSdk } from "./sdkService";
import { config } from "../config/config";
import crypto from "crypto-js";
import { getLogger } from "../utils/logger";
import { getBillingInfo } from "./billingService";
import * as Sentry from "@sentry/browser";
import { isUserAccountProspect } from "./accountStatusService";

/**
 * Checks whether there is a valid token stored locally. In case the token exists, the app will move to logged in state.
 * In case of no token is present but user credentials are stored locally it tries to login to that account in
 * background.
 */
export async function autologin() {
  const sdk = await getSdk();
  const loggedIn = await sdk.authenticationManager.isLoggedIn();
  const logger = getLogger();
  logger.log({ loggedIn }, "autologin");
  if (loggedIn) {
    const result = await sdk.userManager.get({ avoidCache: true });
    //store the globalUserID
    localStorage.setItem("globalUserId", result.globalUserId);
    !isUserAccountProspect(result.settings.accountStatus) && getBillingInfo();
    Sentry.setUser(result);
    return result;
  } else if (localStorage) {
    const encryptedCredentials = localStorage.getItem("credentials");
    if (encryptedCredentials) {
      let credentials = "";
      try {
        credentials = decrypt(encryptedCredentials);
      } catch {
        Sentry.captureMessage("autologin-LoggedOut: Stored credentials didn't decrypt");
        credentials = "{}";
      }
      const { userName, password } = JSON.parse(credentials);
      if (userName && password) {
        try {
          logger.log(`Trying to login using stored credentials for user ${userName}`, "autologin");
          const loginResponse = await login(userName, password);
          return !loginResponse.config && loginResponse;
        } catch (e) {
          Sentry.captureMessage("autologin-LoggedOut: Autologin using stored credentials failed for user.");
          logger.log(`Autologin using stored credentials failed for user ${userName}. Logging out`, "autologin");
          await logout();
        }
      }
    } else {
      const configuration = config.get();
      const { autologin } = configuration.debug;
      if (autologin && autologin.userName && autologin.password) {
        logger.log(`Trying to login using config file credentials for user ${autologin.userName}`, "autologin");
        const loginResponse = await login(autologin.userName, autologin.password);
        return !loginResponse.config && loginResponse;
      }
    }
  }
  return false;
}

/**
 * Uses the SDK to perform a login and in case of being successful, the app store is notified that the user is logged in
 * so that the app UI is updated accordingly.
 * @param {string} username The user name of the account found in local storage
 * @param {string} password The password of the account found in local storage
 * @returns {Promise}
 */
export async function login(username, password, offline = false) {
  try {
    const sdk = await getSdk();
    const loginResponse = await sdk.authenticationManager.login(
      {
        userName: username,
        password: password
      },
      offline
    );
    storeCredentials(username, password);

    // store the globaluserID
    localStorage.setItem("globalUserId", loginResponse.globalUserId);

    // Parallel Call billing api if user logged In and set in Context api for further use
    !isUserAccountProspect(loginResponse.settings.accountStatus) && getBillingInfo(true);
    return loginResponse;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * Logs the current user out and updates the app store accordingly so that the UI can be changed to show the new state
 */
export async function logout(isResetXToken = false) {
  const sdk = await getSdk();
  await sdk.authenticationManager.logout(isResetXToken);
  removeLocalUserData();
}

/**
 * Uses the SDK to perform a signup request with some data provided by the user.
 * @param {String} user - User's account future identifier. For now it will be an e-mail address, but it can be a mobile
 * phone number as well.
 * @param {String} password - User's account password to be able to access to the app when logging in.
 * @returns {Promise}
 */
export async function signup({ user, password }) {
  try {
    const sdk = await getSdk();
    return await sdk.authenticationManager.signup({
      user,
      // NOTE: Password might be optional in future signup implementations and in this case if password param is not received
      // it won't be created in the object sent to the SDK. for now since Signup page component is disabling signup request
      //button until its input value is filled, this 'password' field will be always sent.
      ...(password && { password: password })
    });
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

export async function getUserId() {
  const sdk = await getSdk();
  return await sdk.authenticationManager.getUserId();
}

/**
 * If localStorage is present in the system, the received credentials are encrypted and then stored locally.
 * @param {string} username User name of the account that needs to be stored locally
 * @param {string} password Password of the account that needs to be stored locally
 * @param {string} password Password of the account that needs to be stored locally
 */
export function storeCredentials(username, password) {
  if (localStorage) {
    const credentials = {
      userName: username,
      password: password
    };
    const encryptedCredentials = encrypt(credentials);
    localStorage.setItem("credentials", encryptedCredentials);
    localStorage.removeItem("userRelatedModules");
  }
}

function removeLocalUserData() {
  if (localStorage) {
    localStorage.removeItem("globalUserId");
    localStorage.removeItem("credentials");
    localStorage.removeItem("userRelatedModules");
    localStorage.removeItem("showVoucherPopup");
  }
}

function encrypt(object) {
  return crypto.AES.encrypt(JSON.stringify(object), config.get().authenticationKey);
}

function decrypt(object) {
  return crypto.AES.decrypt(object, config.get().authenticationKey).toString(crypto.enc.Utf8);
}
